import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Transformation } from 'app/model/dataflow/transformation';
import { DataFlowExpressionUtils } from '../expression.util';
import { BaseAsyncAdapter, ITridentResource, ModelMarker } from 'shell-models-lib';
import { ITransformation } from '../IDataflow';
import { GlobalVariables } from 'app/services/globals';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { IntegerDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { PARTITION_FUNCTION_CONSTANT } from 'worker/app-workers/shared/constants';
import { DataFlowAdapterUtils } from '../adapter.util';
import { AzurePostgreSqlTableDataset } from 'app/model/dataset/azure-postgre-sql-table.dataset';
import { isEventHubsSource, isSapOdpSource } from 'app/model/dataflow/dataflow-utils';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { SqlDBSettings } from 'app/model/dataflow/dataset-settings/sql-db-settings';
import { CosmosSqlSettings } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { AdxSettings } from 'app/model/dataflow/dataset-settings/adx-settings';
import { SnowflakeSettings } from 'app/model/dataflow/dataset-settings/snowflake-settings';

export interface IDslScriptable {
    getDslScript(transformation: Transformation): Promise<string>;
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Transformation),
    asynchronous: true
})
export abstract class TransformationAdapter<T extends Transformation>
    extends BaseAsyncAdapter<T, ITransformation>
    implements IDslScriptable
{
    protected _isCombinedScriptEnabled: boolean = !GlobalVariables.splitScriptEnabled;

    public static getPartitionDslScript(transformation: Transformation): string {
        if (
            transformation.partitionOption !== 'Default' &&
            !(transformation.partitionMethod === 'external' && isEventHubsSource(transformation as DataFlowSource))
        ) {
            const partitionMethod = `'${transformation.partitionMethod}'${
                transformation.partitionMethod === 'key' ? ',\n\t\t' : ', '
            }`;
            const partitionExpressions =
                transformation.partitionConditions.length > 0 && transformation.partitionMethod !== 'external'
                    ? `${transformation.partitionConditions
                          .map((condition) =>
                              DataFlowExpressionUtils.wrapExpressionWithEmptyValue(condition.expression)
                          )
                          .join(',\n\t\t')}\n\t`
                    : '';
            let partitionNumber: string;
            if (
                transformation.partitionMethod !== 'external' ||
                !(transformation instanceof DataFlowSource) ||
                (!transformation.useQueryPartition &&
                    !(transformation.sourceDataset instanceof AzurePostgreSqlTableDataset))
            ) {
                partitionNumber = DataFlowExpressionUtils.wrapExpressionConfigValue(
                    transformation.numberOfPartitions,
                    IntegerDataFlowType.TYPE
                );
            } else if (isSapOdpSource(transformation as DataFlowSource)) {
                partitionNumber = (transformation as DataFlowSource).sapOdpPartitionPredicates.templateExpression
                    ? ModelMarker.isNew(transformation)
                        ? '0'
                        : transformation.numberOfPartitions?.expression === '1'
                        ? '1'
                        : '0'
                    : String(
                          (transformation as DataFlowSource).sapOdpPartitionPredicates.expressions.filter(
                              (predicate) => predicate.length
                          ).length
                      );
            } else {
                partitionNumber = String(
                    (transformation as DataFlowSource).partitionPredicates.filter(
                        (predicate) => !stringIsNullOrEmpty(predicate.expression)
                    ).length
                );
            }
            const numberOfPartitions = `${partitionNumber}${partitionExpressions ? ',\n\t\t' : ''}`;
            return `${PARTITION_FUNCTION_CONSTANT}(${partitionMethod}${numberOfPartitions}${partitionExpressions})`;
        }
        return '';
    }

    public abstract getDslScript(transformation: T): Promise<string>;

    /**
     * Appends the database property to the props array for database-type connectors
     * (SQL, Cosmos, ADX, Snowflake) in MDF on Fabric scenarios.
     */
    public static appendDatabaseProp(datasetSettings: DataFlowDatasetSettings, props: string[]): void {
        if (__IS_FABRIC_MDF__ &&
            (
                datasetSettings instanceof SqlDBSettings ||
                datasetSettings instanceof CosmosSqlSettings ||
                datasetSettings instanceof AdxSettings ||
                datasetSettings instanceof SnowflakeSettings
            ) && datasetSettings.databaseName?.expression) {
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(datasetSettings.databaseName)}`);
        }
    }

    protected _getOutputStreams(model: T): string {
        const outputStreams: string[] = [];
        model.outputStreams.forEach((outputStream) => outputStreams.push(outputStream.qualifiedName));
        return outputStreams.join(', ');
    }

    protected _getInputStreams(model: T): string {
        const inputStreams: string[] = [];
        model.inputStreams.forEach((inputStream) => inputStreams.push(inputStream.qualifiedName));
        return inputStreams.join(', ');
    }

    protected _getUnknownProps(transformation: Transformation, transformationData: string): string {
        return DataFlowAdapterUtils.getUnknownProps(transformation, transformationData);
    }

    protected async _toModel(resource: ITransformation, model?: T): Promise<T> {
        model.description = resource.description;
        return model;
    }

    protected async _toResource(transformation: T): Promise<ITransformation> {
        const resource: ITransformation = {
            name: transformation.name,
            description: transformation.description
        };

        if (!this._isCombinedScriptEnabled) {
            resource.script = await this.getDslScript(transformation);
        }

        return resource;
    }

    protected async _toAzureResource(tridentResource: ITridentResource): Promise<never> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }
}
